const viteEnv = import.meta as unknown as { env?: { VITE_API_BASE_URL?: string } };
export const apiBaseUrl = viteEnv.env?.VITE_API_BASE_URL ?? '/v1';

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly requestId?: string,
    public readonly fieldErrors?: Array<{ path: string; message: string }>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let csrfToken: string | undefined;

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  // A FormData body (multipart file upload) must NOT get an explicit content-type -- fetch
  // sets `multipart/form-data; boundary=...` itself from the FormData instance, and forcing
  // `application/json` here would corrupt the request.
  const isFormData = init.body instanceof FormData;
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      credentials: 'include',
      ...init,
      headers: {
        ...(isFormData ? {} : { 'content-type': 'application/json' }),
        ...(csrfToken && init.method && init.method !== 'GET' ? { 'x-csrf-token': csrfToken } : {}),
        ...init.headers,
      },
    });
  } catch {
    // fetch() itself throws (rather than resolving with a response) when the browser never
    // reached the server at all -- offline, DNS failure, a dropped connection, or CORS. That
    // is a distinct situation from a server-returned error and reads better as a connectivity
    // problem than "the request could not be completed."
    throw new ApiError(
      'NETWORK_ERROR',
      "We couldn't connect to the server. Check your connection and try again.",
    );
  }
  if (response.status === 204) return undefined as T;
  const body = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new ApiError(
      body.code ?? 'REQUEST_FAILED',
      body.message ?? "We couldn't complete that request. Please try again.",
      body.requestId,
      body.fieldErrors,
    );
  if (body.csrfToken) csrfToken = body.csrfToken;
  return body as T;
}

/**
 * Replaces a profile photo. This bypasses `api()` (raw image bytes, not JSON) the same way the
 * document-upload flow does, but doesn't need that flow's separate "start intent" round trip
 * first -- there's no R2 object key to reserve ahead of time and no review step, so the whole
 * thing is one authenticated PUT of the file straight to its resource path (`/me/avatar` or
 * `/applicants/:id/avatar`).
 */
export async function uploadAvatar(path: string, file: File): Promise<{ avatarUpdatedAt: string }> {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'content-type': file.type,
        ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
      },
      body: file,
    });
  } catch {
    throw new ApiError(
      'NETWORK_ERROR',
      "We couldn't connect to the server. Check your connection and try again.",
    );
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new ApiError(
      body.code ?? 'REQUEST_FAILED',
      body.message ?? "We couldn't complete that request. Please try again.",
      body.requestId,
      body.fieldErrors,
    );
  if (body.csrfToken) csrfToken = body.csrfToken;
  return body;
}

export type Applicant = {
  id: string;
  name: { firstName: string; middleName: string | null; lastName: string };
  email: string;
  phone: string | null;
  preferredLanguage: 'en' | 'es';
  archivedAt: string | null;
  portalAccount: { status: 'active' | 'invited' | 'suspended' | 'disabled'; lastLoginAt: string | null } | null;
  avatarUpdatedAt: string | null;
  activeApplication: Application | null;
  applications: Application[];
};
export type Application = {
  id: string;
  applicationNumber: number;
  lifecycle: string;
  currentStage: {
    id: string;
    code?: string;
    sortOrder?: number;
    name: string;
  };
  assignments: Assignment[];
};
export type WorkflowStage = {
  id: string;
  code: string;
  sortOrder: number;
  active: boolean;
  translations: {
    en: { name: string };
    es: { name: string };
  };
};
export type StageStatusValue = 'pending' | 'complete' | 'not_applicable';
export type StageChecklistItem = {
  id: string;
  code: string;
  sortOrder: number;
  name: string;
  status: StageStatusValue;
  updatedAt: string | null;
  updatedBy: { id: string; name: string } | null;
};
export type CustomStatus = { name: string; status: StageStatusValue; updatedAt: string };
export type StageChecklist = {
  data: StageChecklistItem[];
  meta: { complete: boolean };
  customStatus: CustomStatus | null;
};
export type StaffApplicationHistory = {
  data: Array<{
    id: string;
    lifecycle: string;
    fromLifecycle: string | null;
    stage: { id: string; code: string; sortOrder: number; name: string } | null;
    fromStage: { id: string; code: string; sortOrder: number; name: string } | null;
    transitionKind:
      | 'created'
      | 'advanced'
      | 'skipped'
      | 'backtracked'
      | 'lifecycle_changed'
      | 'archived'
      | 'reopened';
    reason: string | null;
    applicantVisible: boolean;
    changedBy: { id: string; name: string; role: string } | null;
    changedAt: string;
  }>;
  meta: { page: number; pageSize: number; total: number };
};
export type NotificationEvent = {
  id: string;
  workflowStageId?: string | null;
  eventType: string;
  reason: string | null;
  createdAt: string;
  deliveries: Array<{
    id: string;
    channel: 'email' | 'sms';
    status: string;
    recipient: string;
    body: string;
  }>;
};
export type Assignment = {
  id: string;
  primary: boolean;
  assignedStaff: { id: string; name: string; email: string; role: string };
  unassignedAt: string | null;
};
export type ApplicantList = {
  data: Array<
    Applicant & {
      archived: boolean;
      updatedAt: string;
      primaryAssignment: Assignment['assignedStaff'] | null;
    }
  >;
  meta: { page: number; pageSize: number; total: number };
};
export type NotificationTemplate = {
  id: string;
  code: string;
  channel: 'email' | 'sms';
  direction: 'admin_to_applicant' | 'applicant_to_admin';
  description: string | null;
  active: boolean;
  createdAt: string;
  versions: Array<{
    id: string;
    version: number;
    language: 'en' | 'es';
    subject: string | null;
    body: string;
    published: boolean;
    publishedAt: string | null;
    createdAt: string;
    createdBy: { name: string } | null;
  }>;
};
export type Notice = {
  id: string;
  workflowStageId: string;
  published: boolean;
  translations: {
    en: { title: string; body: string };
    es: { title: string; body: string };
  };
  createdAt: string;
  updatedAt: string;
};
export type StaffUser = {
  id: string;
  email: string;
  name: { firstName: string; middleName: string | null; lastName: string };
  role: 'staff' | 'admin';
  status: 'invited' | 'active' | 'suspended' | 'disabled';
  assignable: boolean;
  lastLoginAt: string | null;
  createdAt: string;
};
export type Session = {
  user: { id: string; name: string; role: 'applicant' | 'staff' | 'admin' };
  expiresAt: string;
  csrfToken: string;
};
export type ApplicantProfile = {
  id: string;
  name: { firstName: string; middleName: string | null; lastName: string };
  email: string;
  phone: string | null;
  preferredLanguage: 'en' | 'es';
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  avatarUpdatedAt: string | null;
};
export type ApplicantApplication = {
  id: string;
  applicationNumber: number;
  lifecycle: 'active' | 'closed' | 'archived';
  currentStage: {
    id: string;
    code: string;
    sortOrder: number;
    name: string;
  };
  submittedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  complete: boolean;
};
export type ApplicantWorkflowStage = {
  id: string;
  code: string;
  sortOrder: number;
  name: string;
  status: StageStatusValue;
};
export type ApplicantHistory = {
  data: Array<{
    id: string;
    lifecycle: string;
    stage: ApplicantApplication['currentStage'];
    transitionKind: string;
    changedAt: string;
  }>;
  meta: { page: number; pageSize: number; total: number };
};
export type ApplicantNotice = {
  id: string;
  title: string;
  body: string;
};
export type StudyMaterial = {
  id: string;
  title: string;
  description: string | null;
  language: 'en' | 'es';
  fileName: string;
  contentLength: number;
  sortOrder: number;
  active: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
};
export type DocumentRequest = {
  id: string;
  mode: 'predefined' | 'custom';
  title: string;
  instructions: string | null;
  status: string;
  dueAt: string | null;
  notesToApplicant: string | null;
  lastReason?: string | null;
  documents: Array<{
    id: string;
    fileName: string;
    status: string;
    uploadedAt: string | null;
  }>;
};
export type Appointment = {
  id: string;
  appointmentTypeId: string;
  typeName: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  status: string;
  bookingUrl: string | null;
  selfServiceCancel: boolean;
  createdAt: string;
  updatedAt: string;
};
export type OutlookConnection = {
  provider: 'microsoft_outlook';
  status: 'disconnected' | 'pending' | 'connected' | 'needs_reauth' | 'failed' | 'disabled';
  configured: boolean;
  displayName: string | null;
  lastVerifiedAt: string | null;
  lastErrorCode: string | null;
  tenantId?: string | null;
  clientId?: string | null;
  clientSecretConfigured?: boolean;
  senderMailbox?: string | null;
  replyTo?: string | null;
  scopes?: string[];
};
export type ClickSendConnection = {
  provider: 'clicksend';
  status: 'disconnected' | 'connected' | 'disabled' | 'needs_reauth' | 'failed';
  configured: boolean;
  displayName: string | null;
  lastVerifiedAt: string | null;
  lastErrorCode: string | null;
  username?: string | null;
  senderId?: string | null;
  defaultCountry?: string | null;
};
export type CalcomConnection = {
  provider: 'calcom';
  configured: boolean;
  status: 'disconnected' | 'connected' | 'disabled' | 'needs_reauth' | 'failed';
  portalBaseUrl: string | null;
  presentation: 'embed' | 'redirect' | null;
  organizationId: string | null;
  teamId: string | null;
  lastErrorCode: string | null;
};
export type AppointmentType = {
  id: string;
  type: 'interview' | 'exam' | null;
  eventTypeId: string;
  eventTypeSlug: string | null;
  name: { en: string; es: string };
  description: { en: string | null; es: string | null };
  durationMinutes: number | null;
  active: boolean;
  selfServiceCancel: boolean;
  selfServiceReschedule: boolean;
  advanceToWorkflowStageId: string | null;
  eligibleStageIds: string[];
  createdAt: string;
  updatedAt: string;
};
export type SharedResource = {
  id: string;
  title: string;
  fileName: string;
  contentType: string;
  contentLength: number;
  uploadedBy: { id: string; name: string } | null;
  uploadedAt: string;
};
export type SchedulingHandoff = {
  available: boolean;
  presentation: 'embed_with_redirect_fallback';
  eventMappings: Array<{
    appointmentTypeId: string;
    name: string;
    description: string | null;
    embedUrl: string;
    fallbackUrl: string;
  }>;
  message: string | null;
};
